import React from "react";

import PropTypes from "prop-types";
import { Swipeable } from "react-swipeable";

import useRbSwipeViewStyles from "./useRbSwipeViewStyles";

function RbSwipeView(props) {
  const { children, activeView, onChange, ...others } = props;

  const childrenCount = React.Children.toArray(children).length;
  const classes = useRbSwipeViewStyles({ childrenCount });
  const rootRef = React.useRef();
  const wrapRef = React.useRef();
  const swippingRef = React.useRef();
  const previousIndexRef = React.useRef();
  const [activeViewInternal, setActiveColumnInternal] = React.useState(0);

  const activeIndex = activeView ?? activeViewInternal;
  const transformBase = `translateX(${
    activeIndex * (100 / childrenCount) * -1
  }%)`;

  const handleSwiping = React.useCallback(
    ({ deltaX, first, dir }) => {
      if (first) {
        swippingRef.current = dir === "Left" || dir === "Right";
      }

      if (!swippingRef.current) {
        return;
      }

      if (activeIndex === 0 && deltaX < 0) {
        return;
      }
      if (activeIndex === childrenCount - 1 && deltaX > 0) {
        return;
      }

      wrapRef.current.style.transform = `${transformBase} translateX(${
        deltaX * -1
      }px)`;
    },
    [activeIndex, transformBase, childrenCount]
  );

  const handleSwiped = React.useCallback(
    ({ deltaX }) => {
      if (!swippingRef.current) {
        return;
      }

      swippingRef.current = false;

      if (activeIndex === 0 && deltaX < 0) {
        return;
      }
      if (activeIndex === childrenCount - 1 && deltaX > 0) {
        return;
      }

      const shouldGotoNext = Math.abs(deltaX) > rootRef.current.clientWidth / 3;
      const nextIndex = !shouldGotoNext
        ? activeIndex
        : activeIndex + Math.sign(deltaX);
      const nextTransform = `translateX(${
        nextIndex * (100 / childrenCount) * -1
      }%)`;

      const animation = wrapRef.current.animate(
        [
          { transform: wrapRef.current.style.transform },
          {
            transform: nextTransform,
          },
        ],
        {
          duration: 100,
        }
      );

      animation.onfinish = (event) => {
        previousIndexRef.current = nextIndex;
        wrapRef.current.style.transform = nextTransform;
        if (activeView !== undefined && onChange !== undefined) {
          onChange(event, nextIndex);
        } else {
          setActiveColumnInternal(nextIndex);
        }
      };
    },
    [activeIndex, activeView, childrenCount, onChange]
  );

  React.useLayoutEffect(() => {
    if (previousIndexRef.current !== undefined && !swippingRef.current) {
      wrapRef.current.animate(
        [
          {
            transform: `translateX(${
              previousIndexRef.current * (100 / childrenCount) * -1
            }%)`,
          },
          {
            transform: `translateX(${
              activeIndex * (100 / childrenCount) * -1
            }%)`,
          },
        ],
        {
          duration: 200,
        }
      );
    }
    previousIndexRef.current = activeIndex;
  }, [activeIndex, childrenCount]);

  return (
    <Swipeable
      {...others}
      innerRef={(elem) => {
        rootRef.current = elem;
      }}
      className={classes.root}
      onSwiping={handleSwiping}
      onSwiped={handleSwiped}
    >
      <div
        ref={wrapRef}
        className={classes.wrap}
        style={{ transform: transformBase }}
      >
        {children}
      </div>
    </Swipeable>
  );
}

RbSwipeView.propTypes = {
  activeView: PropTypes.number,
  onChange: PropTypes.func,
  children: PropTypes.node,
};

RbSwipeView.defaultProps = {
  activeView: undefined,
  onChange: () => {},
  children: null,
};

export default React.memo(RbSwipeView);
